import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Toegankelijke modal-focus: bij openen verspringt de focus naar de dialog, Tab/Shift+Tab
 * blijft binnen de dialog (focus-trap), en bij sluiten gaat de focus terug naar het element
 * dat de pop-up opende. Hang de teruggegeven ref aan het dialoog-paneel (geef het ook
 * `tabIndex={-1}` zodat de fallback-focus werkt).
 */
export function useFocusTrap<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const prevFocused = document.activeElement as HTMLElement | null;
    const visibleFocusables = (): HTMLElement[] =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement,
      );

    (visibleFocusables()[0] ?? node).focus();

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== "Tab") return;
      const els = visibleFocusables();
      if (els.length === 0) { e.preventDefault(); node.focus(); return; }
      const first = els[0]!;
      const last = els[els.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === node)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    node.addEventListener("keydown", onKeyDown);
    return () => {
      node.removeEventListener("keydown", onKeyDown);
      prevFocused?.focus?.();
    };
  }, []);
  return ref;
}
